import React, { useState, useEffect } from 'react';
import { Grid } from '@material-ui/core';
import IndeedCard from './IndeedCard';
import { PrimaryButton, PhoneInput, Text } from 'library';
import EmailInput from '../../public-application/InputFields/EmailInput';
import PasswordInput from '../../public-application/InputFields/PasswordInput';
import NextStepsModal from './NextStepsModal';
import { IndeedFormProps, APP_LINK } from '../IndeedModels';
import { Colors } from '../../../../constants';
import useProviderSignUp from 'api/ProviderSignUpApi';
import usePublicSessionTracking, { PublicSessionEventType } from '../../../shared/PublicSessionTracking';

const IndeedForm = ({ userCreated, onUserCreated }: IndeedFormProps) => {
    const [phoneNumber, setPhoneNumber] = useState<string>('');
    const [displayPhone, setDisplayPhone] = useState<string>('');
    const [isPhone, setIsPhone] = useState<boolean>(true);
    const [email, setEmail] = useState<string>('');
    const [isEmail, setIsEmail] = useState<boolean>(true);
    const [password, setPassword] = useState<string>('');
    const [isPassword, setIsPassword] = useState<boolean>(true);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isDisabled, setIsDisabled] = useState<boolean>(true);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [showError, setShowError] = useState<boolean>(false);

    const completeSignUp = useProviderSignUp();
    const { trackSession } = usePublicSessionTracking('Indeed Form');

    useEffect(() => {
        setIsDisabled(
            !(
                !userCreated &&
                isPhone &&
                phoneNumber.length > 0 &&
                isEmail &&
                email.length > 0 &&
                isPassword &&
                password.length > 0
            ),
        );
        setShowError(false);
    }, [userCreated, isPhone, phoneNumber, isEmail, email, isPassword, password]);

    function getPhoneErrorText() {
        const errorText = !isPhone ? 'Please enter a valid phone number' : '';
        return errorText;
    }

    const handleUserCreated = async () => {
        setIsModalOpen(true);
        onUserCreated();
        trackSession('DIALOG_OPEN', 'Next Steps', PublicSessionEventType.Page);
    };

    const handleDownloadClick = () => {
        trackSession('NAVIGATE_FROM', 'To App Store - Button', PublicSessionEventType.User, { link: APP_LINK });
        window.open(APP_LINK, '_blank');
    };

    const finishApplication = async () => {
        if (isDisabled) return;

        setIsLoading(true);

        try {
            await completeSignUp({
                phoneNumber,
                email,
                password,
                onUserCreated: handleUserCreated,
                shareSource: 'Indeed',
                signupSource: 'Indeed',
                signupRoute: 'IndeedHomePage',
            });
            trackSession('SIGNUP', 'Provider Sign Up - Finished Indeed Application', PublicSessionEventType.User);
        } catch (error) {
            setShowError(true);
        }
        setIsLoading(false);
    };

    return (
        <IndeedCard>
            <Grid container direction="column" spacing={1}>
                <Grid item>
                    <PhoneInput
                        error={!isPhone}
                        errorText={getPhoneErrorText()}
                        label="Phone"
                        value={displayPhone}
                        onChange={(value: string, display: string, isValid: boolean) => {
                            setPhoneNumber(value);
                            setDisplayPhone(display);
                            setIsPhone(isValid);
                        }}
                    />
                </Grid>
                <Grid item>
                    <EmailInput
                        error={!isEmail}
                        errorText={'Please enter your email'}
                        label="Email"
                        value={email}
                        onChange={(value: string, isValid: boolean) => {
                            setIsEmail(isValid);
                            setEmail(value);
                        }}
                    />
                </Grid>
                <Grid item>
                    <PasswordInput
                        error={!isPassword}
                        errorText={
                            'Password must be minimum 8 characters with at least one uppercase letter, one lowercase letter, one digit, and one special character.'
                        }
                        value={password}
                        label="Password"
                        onChange={(value: string, isValid: boolean) => {
                            setPassword(value);
                            setIsPassword(isValid);
                        }}
                    />
                </Grid>
                <Grid item style={{ marginTop: 15 }}>
                    {!userCreated && !showError ? (
                        <PrimaryButton
                            onClick={finishApplication}
                            textStyle={{ fontWeight: 'bold' }}
                            loading={isLoading}
                            disabled={isDisabled}
                        >
                            Finish Application
                        </PrimaryButton>
                    ) : (
                        <PrimaryButton onClick={handleDownloadClick} textStyle={{ fontWeight: 'bold', fontSize: 14 }}>
                            Download the Tandem App
                        </PrimaryButton>
                    )}
                </Grid>
                {showError ? (
                    <Grid item>
                        <Text variant="body2" color={Colors.error}>
                            An error occurred. Please sign up on the app!
                        </Text>
                    </Grid>
                ) : null}
                <Grid item>
                    <NextStepsModal isOpen={isModalOpen} setIsOpen={setIsModalOpen} />
                </Grid>
            </Grid>
        </IndeedCard>
    );
};

export default IndeedForm;
